import { CheckCircle2 } from "lucide-react";
import { SITE, IMAGES } from "../data/siteConfig";
import { useSEO } from "../components/SEO";
import FadeIn from "../components/FadeIn";
import SectionTitle from "../components/SectionTitle";
import FinalCTA from "../components/FinalCTA";

const values = [
  { title: "Convenient", desc: "Easy enquiry and appointment process." },
  { title: "Patient Focused", desc: "Designed around a simple patient experience." },
  { title: "Professional", desc: "Organized sample collection service." },
  { title: "Accessible", desc: "Easy phone and WhatsApp communication." },
  { title: "Convenient Collection", desc: "Ask about home collection availability." },
  { title: "Wide Range", desc: "Enquire about available diagnostic tests." },
];

const AboutPage = () => {
  useSEO(
    "About Us | Dr Lal PathLabs Collection Center Bhopal",
    "About Dr Lal PathLabs Collection Center Bhopal — convenient diagnostic sample collection services with a patient-first approach."
  );

  return (
    <div>
      <section className="bg-[#0B3056] py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-blue-200">About Us</p>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              About {SITE.fullName}
            </h1>
            <p className="mt-4 max-w-2xl text-base text-blue-100 md:text-lg">
              Convenient diagnostic sample collection services for patients in Bhopal, Madhya Pradesh.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <FadeIn>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#0B3056] sm:text-3xl">
              A Patient-First Collection Center
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              {SITE.fullName} is a diagnostic collection center serving patients in Bhopal. We help make laboratory
              testing simpler — from understanding which tests are available, to booking an appointment, to getting
              your sample collected in a professional and hygienic manner.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Patients can visit our collection center or contact us to enquire about home sample collection options in
              Bhopal. Our team supports you with test information, availability, and appointment coordination so you can
              focus on your health.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              This website belongs to an independent collection center and is not the corporate website of Dr Lal
              PathLabs Ltd. For test availability and pricing, please contact us directly.
            </p>
            <ul className="mt-6 space-y-3">
              {["Convenience and easy booking", "Professional sample collection", "Friendly patient support"].map((v) => (
                <li key={v} className="flex items-start gap-3 text-base text-slate-700">
                  <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-[#1E88E5]" aria-hidden="true" />
                  {v}
                </li>
              ))}
            </ul>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="overflow-hidden rounded-2xl shadow-lg">
              <img src={IMAGES.hero} alt="Modern diagnostic laboratory environment" loading="lazy" className="h-full w-full object-cover" />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-[#F0F7FA] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionTitle overline="Our Approach" title="Why Choose Our Collection Center?" align="center" />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {values.map((v, i) => (
              <FadeIn key={v.title} delay={(i % 3) * 0.06} className="h-full">
                <article className="h-full rounded-2xl border border-gray-100 bg-white p-7 shadow-sm" data-testid={`value-card-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <h3 className="font-heading text-lg font-bold text-[#0B3056]">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{v.desc}</p>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
};

export default AboutPage;
